import { useContext, useEffect, useState, useMemo } from 'react';
import {
    Button,
    Label,
    Offcanvas,
    OffcanvasBody,
    OffcanvasHeader,
} from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { format, parseISO } from 'date-fns';
import { DateRangeColumnFilter, dateBetweenFilterFn } from '~/services/filters';
import { downloadCSV } from '~/services/csv';
import { CampaignContext } from '~/context/campaign';
import {
    fetchAllSupporters,
    fetchMyCampaign,
    fetchSupporters,
} from '~/services/campaign';
import {
    IDonationItem,
    IDonationSummary,
    IRefundSummary,
    MyCampaignResponse,
} from '~/types/campaign';
import { formatCurrency } from '~/services/helper';
import TableContainer from './components/TableContainer';
import { Loading } from '~/shared/components/Loading';
import { CampaignHeaderSection } from '~/shared/components/cards/CampaignItemsCard';
import { TextInput } from '~/shared/components/form/Inputs';
import { renderIcon } from '~/shared/components/icons';
import { ColumnDef } from '@tanstack/react-table';
import { useToasts } from 'react-toast-notifications';
import { NoDataCard } from './components/NoDataCard';

const TITLE = 'Your campaign does not have any supporters yet';
const BODY = `Once we have received and processed an initial donation for your campaign, campaign supporters will start to appear here.`;

function SupportersText() {
    return (
        <div>
            <h1 className='h2'>Supporters</h1>
            <p>
                Donations will not appear here or be reflected on your
                fundraising thermometer until they have been processed by our
                office. Please allow 5-8 business days for donations to be
                processed.
            </p>
            <p>
                We respect donor privacy. Donors can choose to keep their name
                or the amount of their donation private.{' '}
            </p>
            <p>
                If a donation has been refunded, the Refunded column shows what
                was returned. A fully refunded donation stays on this list with
                no contribution amount.
            </p>
            <p>
                <strong>✓ - DONOR COVERED FEES</strong>
            </p>
        </div>
    );
}

const defaultFilters = { name_query: '', to: null, from: null };

/** The Refunded column's value for one exported row. */
const refundedForExport = (supporter: IDonationItem) => {
    const summary = supporter.refund_summary;
    if (!summary) {
        return '';
    }
    if (!summary.count && summary.pending_count) {
        return 'Pending';
    }
    return summary.total_refunded
        ? formatCurrency(summary.total_refunded)
        : '-';
};

/**
 * What came back on a refunded gift.
 *
 * A fully refunded gift shows a dash in the Contribution column — nothing of it
 * remains — so this column is the only thing distinguishing it from a donation
 * whose donor kept the amount private. It always says what happened, and falls
 * back to the bare word when the donor hid their amount, since the refund
 * figure would give the original away.
 */
const RefundCell = ({ summary }: { summary?: IRefundSummary | null }) => {
    if (!summary) {
        return <></>;
    }

    const { is_full_refund, total_refunded, original_amount } = summary;

    if (!summary.count && summary.pending_count) {
        return <span className='text-muted'>Refund pending</span>;
    }
    if (!total_refunded) {
        return (
            <span className='fw-bold'>
                {is_full_refund ? 'Fully refunded' : 'Refunded'}
            </span>
        );
    }
    return (
        <span className='fw-bold'>
            {formatCurrency(total_refunded)}
            {!is_full_refund && original_amount && (
                <span className='fw-normal'>
                    {' '}
                    of {formatCurrency(original_amount)}
                </span>
            )}
        </span>
    );
};
type FormattedAddress = {
    address_1: string;
    city_state: string;
    state: string;
    zip_code: string;
};

type Filter = {
    name_query?: string | null;
    to?: string | null;
    from?: string | null;
};

export const Supporters = ({}: any) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [filterDrawer, setFilterDrawer] = useState<boolean>(false);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [supporters, setSupporters] = useState<IDonationSummary>();
    const history = useHistory();
    const { addToast } = useToasts();
    const [tableLoader, setTableLoader] = useState<boolean>(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const {
        control,
        handleSubmit,
        getValues,
        formState: { isSubmitting },
    } = useForm({
        defaultValues: defaultFilters,
    });

    const FormattedAddress = ({
        address_1,
        city_state,
        state,
        zip_code,
    }: FormattedAddress) => {
        return address_1 ? (
            <span>
                {address_1}
                <br />
                {city_state}, {state} {zip_code}
            </span>
        ) : (
            <></>
        );
    };

    const data = useMemo(() => {
        if (supporters) {
            return supporters?.results?.map((supporter) => {
                const { address_1, city_state, state, zip_code } = supporter;

                return {
                    name: supporter.name,
                    date: format(parseISO(supporter.gift_date), 'MM/dd/yy'),
                    amount: supporter.gift_amount
                        ? formatCurrency(supporter.gift_amount)
                        : '-',
                    refund_summary: supporter.refund_summary,
                    fees_covered: supporter.covered_fees,
                    email: supporter.email,
                    address_1: address_1,
                    city_state: city_state,
                    state: state,
                    zip_code: zip_code,
                };
            });
        } else {
            return [];
        }
    }, [supporters]);

    const columns = useMemo<ColumnDef<IDonationItem>[]>(
        () => [
            {
                id: 'name',
                header: 'Name',
                accessorKey: 'name', // accessor is the "key" in the data
                enableSorting: false, // this is a necessary prop within react-table FYI
                accessorFn: (props) => {
                    return <div className='fw-bold'>{props.name}</div>;
                },
            },
            {
                id: 'date',
                header: 'Date',
                accessorKey: 'date',
                enableSorting: true,
                Filter: DateRangeColumnFilter,
                filterFn: dateBetweenFilterFn,
            },
            {
                id: 'contribution',
                header: 'Contribution',
                accessorKey: 'amount',
                enableSorting: true,
            },
            {
                id: 'refunded',
                header: 'Refunded',
                accessorKey: 'refund_summary',
                enableSorting: false,
                accessorFn: (props) => {
                    return <RefundCell summary={props.refund_summary} />;
                },
            },
            {
                id: 'fees_covered',
                header: 'Fees Covered',
                accessorKey: 'fees_covered',
                enableSorting: false,
                accessorFn: (props) => {
                    const feesCovered = Boolean(props.fees_covered);
                    return feesCovered ? renderIcon('check') : '';
                },
            },
            {
                id: 'email',
                header: 'Email',
                accessorKey: 'email',
                enableSorting: false,
                size: 270,
                minSize: 100,
                maxSize: 300,
                accessorFn: (props) => {
                    return <a href={`mailto:${props.email}`}>{props.email}</a>;
                },
            },
            {
                id: 'address',
                header: 'Address',
                accessorKey: 'address',
                enableSorting: false,
                accessorFn: (props) => {
                    return (
                        <FormattedAddress
                            address_1={props.address_1}
                            city_state={props.city_state}
                            state={props.state}
                            zip_code={props.zip_code}
                        />
                    );
                },
            },
        ],
        []
    );

    useEffect(() => {
        if (!campaign) {
            getMyCampaign();
        }
    }, []);

    useEffect(() => {
        if (campaign) {
            setLoading(true);
            console.log('Getting supporters');
            getSupporters();
        }
    }, [campaign, page, pageSize]);

    const onPageNumberChange = (page: number) => {
        setTableLoader(true);
        setPage(page);
    };

    const getSupporters = async () => {
        const payload = getValues();
        if (campaign) {
            try {
                const ntaf_id = campaign.campaignDetail.ntaf_id;
                if (!ntaf_id) {
                    console.log('No ntaf id');
                    return;
                }
                if (payload.name_query && page !== 1) {
                    setPage(1);
                } else {
                    const resp = await fetchSupporters(
                        ntaf_id,
                        payload,
                        page,
                        pageSize
                    );
                    setSupporters(resp);
                }
            } catch (e) {
                addToast(
                    'An error occurred while fetching supporters. Please try again later.',
                    { appearance: 'error' }
                );
            } finally {
                setLoading(false);
                setTableLoader(false);
            }
        }
    };

    const getMyCampaign = async () => {
        try {
            const resp: MyCampaignResponse = await fetchMyCampaign();
            if (resp.detail && resp.detail === 'Invalid token.') {
                // Delete localStorage token
                localStorage.removeItem('token');
                history.push('/login');
            }
            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
            setLoading(false);
        } catch (e) {
            addToast(
                'An error occurred while fetching campaign. Please try again later.',
                { appearance: 'error' }
            );
        }
    };

    const onSubmit = async (data: Filter) => {
        setLoading(true);
        getSupporters();
    };

    const supportersExport = useMemo(() => {
        if (supporters) {
            return supporters?.results?.map((supporter) => {
                const {
                    address_1,
                    address_2,
                    city_state,
                    state,
                    zip_code,
                    country,
                } = supporter;
                return {
                    GiftDate: format(parseISO(supporter.gift_date), 'MM/dd/yy'),
                    Name: supporter.name,
                    Amount:
                        supporter.donor_share_amount === 1
                            ? formatCurrency(supporter.gift_amount)
                            : '-',
                    Refunded: refundedForExport(supporter),
                    AddressLine1: address_1,
                    AddressLine2: address_2,
                    City: city_state,
                    State: state,
                    Zip: zip_code,
                    Country: country,
                    Email: supporter.email,
                };
            });
        } else {
            return [];
        }
    }, [supporters]);

    if (!campaign) {
        return <Loading />;
    }

    const { campaignCoordinator } = campaign;

    return (
        <div className='container__inner'>
            <CampaignHeaderSection
                leftcard={<SupportersText />}
                contact={campaignCoordinator}
            />
            <div className='filter-bar flex-column flex-md-row justify-content-xl-between align-items-md-center gap-3 flex-wrap'>
                <div className='d-none d-lg-flex gap-2 align-items-center flex-shrink-0'>
                    <div className='d-flex gap-2 align-items-lg-center flex-grow-1'>
                        <Label
                            className='fw-light'
                            style={{ textTransform: 'none' }}
                        >
                            Search Name
                        </Label>
                        <TextInput
                            name='name_query'
                            label=''
                            type='text'
                            control={control}
                            required={false}
                        />
                    </div>
                    <div className='d-none d-lg-flex gap-2 align-items-center'>
                        <Label
                            className='fw-light'
                            style={{ textTransform: 'none' }}
                        >
                            Show donations from
                        </Label>
                        <TextInput
                            name='from'
                            label=''
                            type='date'
                            control={control}
                            required={false}
                        />
                    </div>
                    <div className='d-none d-lg-flex gap-2 align-items-center'>
                        <Label
                            className='fw-light'
                            style={{ textTransform: 'none' }}
                        >
                            to
                        </Label>
                        <TextInput
                            name='to'
                            label=''
                            type='date'
                            control={control}
                            required={false}
                        />
                    </div>
                    <div className='d-none d-lg-block'>
                        <Button
                            color='outline-dark'
                            size='sm'
                            onClick={handleSubmit(onSubmit)}
                            disabled={isSubmitting || loading}
                        >
                            Filter
                        </Button>
                    </div>
                </div>

                <Button
                    color='outline-dark'
                    size='sm'
                    className='d-none d-lg-inline-block'
                    onClick={async () => {
                        const ntaf_id = campaign.campaignDetail.ntaf_id;
                        const filters = getValues();
                        const allData = await fetchAllSupporters(
                            ntaf_id,
                            filters
                        );
                        console.log('allData: ', allData);
                        downloadCSV(
                            allData,
                            `${campaign.campaignDetail.campaign_name} Campaign Supporters`
                        );
                    }}
                >
                    {renderIcon('download')} Export List
                </Button>

                <Button
                    color='outline-dark'
                    className='d-block d-lg-none w-100 m-0'
                    onClick={() => setFilterDrawer(true)}
                >
                    Filters
                </Button>
                <Offcanvas
                    direction='end'
                    fade={false}
                    isOpen={filterDrawer}
                    toggle={() => {
                        setFilterDrawer(false);
                    }}
                >
                    <OffcanvasHeader
                        className='text-uppercase'
                        toggle={() => {
                            setFilterDrawer(false);
                        }}
                    >
                        Filters
                    </OffcanvasHeader>
                    <OffcanvasBody>
                        <div className='d-flex flex-column gap-3 justify-content-between h-100 pb-4'>
                            <div>
                                <div className='d-flex flex-column gap-2'>
                                    <Label
                                        className='fw-light'
                                        style={{ textTransform: 'none' }}
                                    >
                                        Search Name
                                    </Label>
                                    <TextInput
                                        name='name_query'
                                        label=''
                                        type='text'
                                        control={control}
                                        required={false}
                                    />
                                </div>
                                <div className='d-flex flex-column gap-2'>
                                    <Label
                                        className='fw-light'
                                        style={{ textTransform: 'none' }}
                                    >
                                        Show donations from
                                    </Label>
                                    <TextInput
                                        name='from'
                                        label=''
                                        type='date'
                                        control={control}
                                        required={false}
                                    />
                                </div>
                                <div className='d-flex flex-column gap-2'>
                                    <Label
                                        className='fw-light'
                                        style={{ textTransform: 'none' }}
                                    >
                                        to
                                    </Label>
                                    <TextInput
                                        name='to'
                                        label=''
                                        type='date'
                                        control={control}
                                        required={false}
                                    />
                                </div>

                                <Button
                                    color='outline-dark'
                                    size='sm'
                                    className='d-block w-100'
                                    onClick={async () => {
                                        // const ntaf_id = campaign.campaignDetail.ntaf_id;
                                        // const filters = getValues();
                                        // const allData = await fetchAllSupporters(ntaf_id, filters);
                                        downloadCSV(
                                            supportersExport,
                                            `${campaign.campaignDetail.campaign_name} Campaign Supporters`
                                        );
                                    }}
                                >
                                    {renderIcon('download')} Export List
                                </Button>
                            </div>

                            <div className='sticky-bottom'>
                                <Button
                                    color='outline-dark'
                                    size='sm'
                                    className='d-block w-100'
                                    onClick={handleSubmit(onSubmit)}
                                    disabled={isSubmitting || loading}
                                >
                                    Filter
                                </Button>
                            </div>
                        </div>
                    </OffcanvasBody>
                </Offcanvas>
            </div>

            {loading || isSubmitting ? (
                <Loading fullscreen={false} />
            ) : (
                <div className='pb-5'>
                    <TableContainer
                        showHeaders
                        columns={columns}
                        data={data}
                        showPaginationComponents={true}
                        showFilterComponents={false}
                        sort={false}
                        NoDataCard={<NoDataCard title={TITLE} body={BODY} />}
                        page={page}
                        pageCount={
                            supporters?.count
                                ? Math.ceil(supporters.count / pageSize)
                                : 1
                        }
                        tableLoader={tableLoader}
                        setPage={onPageNumberChange}
                        setPageSize={setPageSize}
                        pageSize={pageSize}
                        isServerSidePagination={true}
                    />
                </div>
            )}
        </div>
    );
};
